// Parallel submit to Google Sheets while the inquiry form runs (Zoho still proceeds).
// Non-blocking & INP-friendly.

import { getCookie, setCookie, updateCookie, text_truncate, getBrowserType } from "../core/utils.js";
import { VisitorID } from "./visitor-id.js";

function generateShortcode() {
    const d = new Date();
    return (
        d.getDate().toString().padStart(2, "0") +
        (d.getMonth() + 1).toString().padStart(2, "0") +
        d.getFullYear().toString().slice(2) +
        d.getHours().toString().padStart(2, "0") +
        d.getMinutes().toString().padStart(2, "0") +
        d.getSeconds().toString().padStart(2, "0") +
        d.getMilliseconds()
    );
}

// Ambil IP dari input hidden bernama "IP Address"
function getExternalIP() {
    const el = document.querySelector('input[name="IP Address"]');
    return el ? (el.value || "").trim() : "";
}

// Sama seperti di button trial: update cookie campaign_info → utm_campaign = [Req.D...]
function updateCampaignInfoWithReq(click_sh) {
    let ci = { gclid: "", utm_source: "", utm_medium: "", utm_campaign: "" };
    const raw = getCookie("campaign_info");
    if (raw) {
        try {
            ci = JSON.parse(decodeURIComponent(raw));
        } catch {}
    }
    ci.utm_campaign = `[Req.O${click_sh}]`;
    const enc = encodeURIComponent(JSON.stringify(ci));
    if (getCookie("created_at")) updateCookie("campaign_info", enc);
    else setCookie("campaign_info", enc, 31);
    return ci;
}

// Build payload: izinkan override utm_campaign (hasil [Req.D...])
function buildPayload(submitter, opts = {}) {
    const { utmCampaignOverride, shortId } = opts;
    const urlNow = new URL(window.location.href);

    const gclid = urlNow.searchParams.get("gclid") || getCookie("gclid") || "";
    const msclkid = urlNow.searchParams.get("msclkid") || getCookie("msclkid") || "";

    const utm_source = urlNow.searchParams.get("utm_source") || getCookie("utm_source") || "";
    const utm_medium = urlNow.searchParams.get("utm_medium") || getCookie("utm_medium") || "";
    const utm_content = urlNow.searchParams.get("utm_content") || getCookie("utm_content") || "";
    const browser = getBrowserType();
    const wabaMedium = getCookie("waba_code") || "";

    const ipAddress = getExternalIP();
    const shortCode = shortId || generateShortcode();
    const gclidJoined = text_truncate(`${gclid}|${msclkid}`, 250, "");
    const journey = text_truncate(getCookie("user_journey"), 250, "");
    const utm_term = urlNow.searchParams.get("utm_term") || getCookie("utm_term") || "";
    const matchtype = urlNow.searchParams.get("matchtype") || getCookie("matchtype") || "";
    const device = /mobile/i.test(navigator.userAgent) ? "Mobile" : "Desktop";
    let btnId = "",
        btnLabel = "";
    if (submitter) {
        btnId = submitter.id || "";
        btnLabel = (submitter.getAttribute("data-label") || submitter.innerText || submitter.value || "").trim();
    }
    const gaid = getCookie("gaid") || getCookie("_ga") || "";
    const utm_campaign = urlNow.searchParams.get("utm_campaign") || getCookie("utm_campaign") || "";
    const bannerId = getCookie("banner_click") || "";
    const domain = window.location.hostname;
    const gclidJourney = text_truncate(getCookie("gclid_journey"), 250, "");
    const phoneNumber = text_truncate(getCookie("phone_number"), 250, "");
    const visitorId = VisitorID.init() || getCookie("visitor_id") || "";

    return {
        "IP Address": ipAddress,
        Short: shortCode,
        GCLID: gclidJoined,
        Journey: journey,
        "UTM Term": utm_term,
        Matchtype: matchtype,
        Device: device,
        "Button Label and Button ID": `ID: ${btnId}, Label: ${btnLabel}`,
        GAID: gaid,
        "UTM Campaign": utm_campaign,
        "UTM Source": utm_source,
        "UTM Medium": utm_medium,
        "Banner ID": bannerId,
        Domain: domain,
        "GCLID Journey": gclidJourney,
        "Phone Number": phoneNumber,
        "Visitor ID": visitorId,
    };
}

// Rate limiting: tolak jika sudah submit >= maxPerHour kali dalam 1 jam terakhir
function checkRateLimit(maxPerHour) {
    try {
        const key = "_inq_rl";
        const now = Date.now();
        const stored = localStorage.getItem(key);
        const times = stored ? JSON.parse(stored).filter((t) => now - t < 3_600_000) : [];
        if (times.length >= maxPerHour) return false;
        times.push(now);
        localStorage.setItem(key, JSON.stringify(times));
        return true;
    } catch {
        return true; // fail open jika localStorage tidak tersedia
    }
}

// Honeypot: return true jika field isi (kemungkinan bot)
function isHoneypotFilled(form, fieldName) {
    if (!fieldName) return false;
    const hp = form.querySelector(`[name="${fieldName}"]`);
    return hp != null && hp.value.trim() !== "";
}

function sendToSheet(url, payload) {
    console.log("[waba-inquiry] sendToSheet payload:", payload);
    try {
        const body = new Blob([new URLSearchParams(payload)], {
            type: "application/x-www-form-urlencoded;charset=UTF-8",
        });
        if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return true;
    } catch {}
    try {
        fetch(url, {
            method: "POST",
            body: new URLSearchParams(payload),
            keepalive: true,
            credentials: "omit",
            mode: "no-cors",
            cache: "no-store",
        }).catch(() => {});
        return true;
    } catch {
        return false;
    }
}

/**
 * Init parallel tracking untuk inquiry form (Zoho).
 */
export function initInquiryParallelSubmit(opts = {}) {
    const {
        formSelector = 'form[data-inquiry-tracking="true"]',
        scriptURL = "https://script.google.com/macros/s/AKfycbx7Kuq-HW-DhQHQu45CErX6QlrNe9_EO0AIQlQEXONqJyL_8T-ohYdRDD_PJyVKJCtYuA/exec",
        buttonSelectors = ["#submit-inquiry-form", ".btn-submit"],
        // Security options
        honeypotField = "",        // nama field honeypot di HTML (kosong = nonaktif)
        maxSubmissionsPerHour = 3, // max submit per jam per browser
        minFillMs = 1500,          // minimum waktu isi form dalam ms (anti-bot instan)
    } = opts;

    const forms = Array.from(document.querySelectorAll(formSelector));
    if (!forms.length) return;

    forms.forEach((form) => {
        let sent = false;
        let firstInteractionAt = null;

        // Catat waktu pertama user menyentuh form (timing check)
        const trackFirstInteraction = () => {
            if (!firstInteractionAt) firstInteractionAt = Date.now();
        };
        form.addEventListener("focusin", trackFirstInteraction, { once: true, passive: true });
        form.addEventListener("input", trackFirstInteraction, { once: true, passive: true });

        // Cek semua kondisi bot-detection; return true = blokir
        const shouldBlock = (formEl) => {
            if (isHoneypotFilled(formEl, honeypotField)) return true;
            if (firstInteractionAt && Date.now() - firstInteractionAt < minFillMs) return true;
            if (!checkRateLimit(maxSubmissionsPerHour)) return true;
            return false;
        };

        const markSent = () => {
            sent = true;
            setTimeout(() => {
                sent = false;
            }, 5000);
        };

        // fungsi kecil untuk apply override ke cookie + update hidden input form
        const applyReqOverride = () => {
            const shortCode = generateShortcode();
            const ci = updateCampaignInfoWithReq(shortCode); // set cookie utm_campaign = [Req.D...]
            // kalau ada hidden utm_campaign di form, set juga
            const hiddenUtm = form.querySelector('input[name="utm_campaign"], input#utm_campaign');
            if (hiddenUtm) hiddenUtm.value = ci.utm_campaign;
            return { shortCode, utmReq: ci.utm_campaign };
        };

        // CLICK hook (type="button")
        buttonSelectors.forEach((sel) => {
            const btns = Array.from(form.querySelectorAll(sel));
            btns.forEach((btn) => {
                btn.addEventListener(
                    "click",
                    () => {
                        if (sent) return;
                        // Skip sending to Sheets if form is not valid yet;
                        // the submit handler will show validation errors.
                        if (!form.checkValidity()) return;
                        // Bot detection: honeypot / timing / rate limit
                        if (shouldBlock(form)) return;

                        const { shortCode } = applyReqOverride(); // Zoho: hidden utm_campaign sudah di-set [Req.D...]
                        // Sheet: kirim utm_campaign ASLI
                        const payload = buildPayload(btn, {
                            shortId: shortCode,
                        });
                        sendToSheet(scriptURL, payload);
                        markSent();
                    },
                    { passive: true }
                );
            });
        });

        // SUBMIT hook (type="submit" / Enter)
        form.addEventListener("submit", (ev) => {
            // Always prevent first so we control the flow.
            ev.preventDefault();

            // Validate required fields, patterns, email types, etc.
            if (!form.checkValidity()) {
                form.reportValidity();
                return;
            }

            if (sent) {
                // click handler sudah kirim ke Sheets, biarkan form submit lanjut
                form.submit();
                return;
            }

            // Bot detection: honeypot / timing / rate limit
            // Jika terblokir, form.submit() tetap dipanggil agar Zoho tetap jalan,
            // tapi data tidak dikirim ke Sheets.
            const blocked = shouldBlock(form);

            if (!blocked) {
                const { shortCode } = applyReqOverride();
                const submitter = ev.submitter || form.querySelector('[type="submit"], [type="button"].btn-submit');
                const payload = buildPayload(submitter, { shortId: shortCode });
                sendToSheet(scriptURL, payload);
                markSent();
            }

            // Beri waktu sendBeacon ter-queue, lalu submit programmatically
            setTimeout(() => form.submit(), blocked ? 0 : 100);
        });
    });
}
